/**
 * Page awareness for the help assistant.
 *
 * The assistant tells the backend *where* the user is, never *what* they are
 * looking at. Everything that could carry a record leaves the client:
 *  - path segments that look like an id (uuid, numeric, long opaque token)
 *    become `:id`
 *  - query parameters are dropped unless both the name AND the value are on
 *    the whitelist below (`/shifts?view=checklist`, `/settings?tab=...`)
 *  - hashes, DOM content and component state are never touched
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Public/signature tokens: long, mixed, no dashes-as-words. */
const OPAQUE_TOKEN_RE = /^[A-Za-z0-9_-]{20,}$/;

const looksLikeId = (segment: string): boolean =>
  UUID_RE.test(segment) || /^\d+$/.test(segment) || OPAQUE_TOKEN_RE.test(segment);

/** `/patients/7f3.../overview` -> `/patients/:id/overview`. */
export const maskPathname = (pathname: string): string => {
  const segments = pathname
    .split('/')
    .filter(Boolean)
    .map(segment => (looksLikeId(segment) ? ':id' : segment));
  return segments.length ? `/${segments.join('/')}` : '/';
};

/**
 * Query parameters that may leave the browser, per masked path.
 * Only these exact name/value pairs are forwarded — anything else (search
 * terms, ids, filters) is dropped.
 */
const SAFE_QUERY: Record<string, Record<string, readonly string[]>> = {
  '/shifts': { view: ['checklist'] },
  '/settings': {
    tab: [
      'agency',
      'invoice',
      'language',
      'theme',
      'emailTemplate',
      'signatureEmailTemplate',
      'invoiceEmailTemplate',
      'mahnungEmailTemplate',
      'freigegebenEmailTemplate',
      'customEmailTemplates',
      'smtpSettings',
      'emailTriage',
      'manacareImport',
      'checklistTemplates',
      'documentTypes',
      'knowledgeTypes',
      'patientDocumentTypes',
      'actionLogs',
      'advanced',
    ],
  },
};

/** `?view=checklist&clientId=123` on /shifts -> `?view=checklist`. */
export const maskSearch = (maskedPath: string, search: string): string => {
  const allowed = SAFE_QUERY[maskedPath];
  if (!allowed || !search) return '';
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const kept: string[] = [];
  Object.keys(allowed).forEach(name => {
    const value = params.get(name);
    if (value && allowed[name].includes(value)) kept.push(`${name}=${value}`);
  });
  return kept.length ? `?${kept.join('&')}` : '';
};

/**
 * Route -> pageKey. The key with a query suffix wins over the plain path, so
 * `/shifts?view=checklist` is its own page for the assistant.
 */
export const PAGE_KEY_BY_ROUTE: Record<string, string> = {
  '/': 'home',
  '/dashboard': 'dashboard',

  '/patients': 'clients',
  '/patients/:id/overview': 'clientDetails',
  '/patient-profiles': 'clientProfiles',

  '/caregivers': 'caregivers',
  '/caregivers/:id/overview': 'caregiverDetails',

  '/shifts': 'shifts',
  '/shifts?view=checklist': 'checklist',

  '/invoices': 'invoices',
  '/emails': 'emails',
  '/email-triage': 'emailTriage',

  '/documents': 'documentation',
  '/herzensapp': 'documentation',
  '/dgkp': 'dgkp',
  '/zeiterfassung': 'timeTracking',

  '/templates': 'templates',
  '/pdf-autofill': 'pdfAutoFill',
  '/tasks': 'tasks',
  '/knowledge': 'knowledge',
  '/files': 'files',
  '/staff': 'staff',

  '/settings': 'settings',
  '/settings?tab=agency': 'settingsAgency',
  '/settings?tab=invoice': 'settingsInvoice',
  '/settings?tab=language': 'settingsLanguage',
  '/settings?tab=theme': 'settingsTheme',
  '/settings?tab=emailTemplate': 'settingsEmailTemplates',
  '/settings?tab=signatureEmailTemplate': 'settingsEmailTemplates',
  '/settings?tab=invoiceEmailTemplate': 'settingsEmailTemplates',
  '/settings?tab=mahnungEmailTemplate': 'settingsEmailTemplates',
  '/settings?tab=freigegebenEmailTemplate': 'settingsEmailTemplates',
  '/settings?tab=customEmailTemplates': 'settingsEmailTemplates',
  '/settings?tab=smtpSettings': 'settingsSmtp',
  '/settings?tab=emailTriage': 'settingsEmailTriage',
  '/settings?tab=checklistTemplates': 'settingsChecklistTemplates',
  '/settings?tab=documentTypes': 'settingsDocumentTypes',
  '/settings?tab=knowledgeTypes': 'settingsKnowledgeTypes',
  '/settings?tab=patientDocumentTypes': 'settingsDocumentTypes',
  '/settings?tab=actionLogs': 'settingsActionLogs',
  '/settings?tab=advanced': 'settingsAdvanced',
  '/settings/manacare-import': 'manacareImport',

  '/matching': 'matching',
  '/matching/new': 'matchingNew',
  '/matching/:id': 'matchingDetails',

  '/billing': 'billing',
  '/agencies': 'agencies',
  '/agency-menu-config': 'agencyMenuConfig',
  '/admin-dashboard': 'adminDashboard',
  '/admin/billing': 'adminBilling',
  '/admin/email-triage': 'adminEmailTriage',
  '/admin/herzensapp': 'adminHerzensapp',
  '/admin/feedback': 'adminFeedback',
  '/admin/testimonials': 'adminTestimonials',

  '/doc': 'docs',
};

export const UNKNOWN_PAGE_KEY = 'unknown';

export interface HelpRouteContext {
  /** Coarse page identity the backend keys its suggestions on. */
  pageKey: string;
  /** Masked route, safe to send: `/patients/:id/overview`, `/shifts?view=checklist`. */
  currentRoute: string;
}

export const buildHelpRouteContext = (
  pathname: string,
  search = '',
): HelpRouteContext => {
  const maskedPath = maskPathname(pathname);
  const maskedSearch = maskSearch(maskedPath, search);
  const currentRoute = `${maskedPath}${maskedSearch}`;
  const pageKey =
    PAGE_KEY_BY_ROUTE[currentRoute] ??
    PAGE_KEY_BY_ROUTE[maskedPath] ??
    // Every handbook page (/doc/<chapter>/<page>) is the handbook.
    (maskedPath.startsWith('/doc/') ? 'docs' : UNKNOWN_PAGE_KEY);
  return { pageKey, currentRoute };
};

/** Routes the widget must never appear on (no login, no public links). */
const PUBLIC_PREFIXES = [
  '/login',
  '/accept-invitation',
  '/public-signature',
  '/onboarding',
  '/match',
  '/newsletter-abmelden',
];

export const isPublicHelpRoute = (pathname: string): boolean =>
  PUBLIC_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));
